// ============================================================================
// CASE TYPE FORM MODAL — Add / Edit Case Type (MSTR-CASETYPE-001) — Phase 8a
// ============================================================================
// Registry of litigation case types. Each row binds a case type to its EWF
// workflow definition (soft FK by code) and to its Phase 9 frontend
// detail-panel registry key.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { caseTypeService } from 'api/services/caseTypeService';
import type { CaseType } from 'api/types/caseType.types';

type CaseTypeStatus = 'ACTIVE' | 'INACTIVE';
type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface CaseTypeFormData {
  caseTypeCode: string;
  displayName: string;
  description: string;
  iconName: string;
  workflowDefinitionCode: string;
  detailPanelCode: string;
  defaultModalSize: ModalSize;
  isSecuredOnly: boolean;
  displayOrder: number;
  statusCode: CaseTypeStatus;
}

const INITIAL_FORM_DATA: CaseTypeFormData = {
  caseTypeCode: '',
  displayName: '',
  description: '',
  iconName: '',
  workflowDefinitionCode: '',
  detailPanelCode: '',
  defaultModalSize: 'xl',
  isSecuredOnly: false,
  displayOrder: 100,
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE',   label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const MODAL_SIZE_OPTIONS = [
  { value: 'sm', label: 'Small (400px)' },
  { value: 'md', label: 'Medium (600px)' },
  { value: 'lg', label: 'Large (800px)' },
  { value: 'xl', label: 'XL (1000px) — recommended' },
];

export interface CaseTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: CaseType | null;
  onSaved: () => void;
  onClose: () => void;

  // Minimize support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const CaseTypeFormModal: React.FC<CaseTypeFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<CaseTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const recordRef = useRef(record);
  recordRef.current = record;

  const initialSnapshotRef = useRef('');

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: CaseTypeFormData = rec
      ? {
          caseTypeCode: rec.caseTypeCode,
          displayName: rec.displayName,
          description: rec.description ?? '',
          iconName: rec.iconName ?? '',
          workflowDefinitionCode: rec.workflowDefinitionCode ?? '',
          detailPanelCode: rec.detailPanelCode ?? '',
          defaultModalSize: rec.defaultModalSize,
          isSecuredOnly: rec.isSecuredOnly,
          displayOrder: rec.displayOrder,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as CaseTypeStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.caseTypeCode || currentFormData.caseTypeCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.caseTypeCode?.trim();
      const name = currentFormData.displayName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.caseTypeCode || currentRecord?.caseTypeCode || 'Case Type'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.caseTypeCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.caseTypeCode.trim();
    const name = formData.displayName.trim();

    if (!code) errors.caseTypeCode = 'Case type code is required';
    else if (code.length > 40) errors.caseTypeCode = 'Max 40 characters';
    else if (!/^[A-Z][A-Z0-9_]*$/.test(code))
      errors.caseTypeCode = 'Must start with a letter; uppercase letters, digits, underscores only';

    if (!name) errors.displayName = 'Display name is required';
    else if (name.length > 100) errors.displayName = 'Max 100 characters';

    if (formData.iconName && !/^[a-z][a-zA-Z0-9]*$/.test(formData.iconName))
      errors.iconName = 'Must be camelCase (letters and digits only)';

    if (formData.workflowDefinitionCode && !/^[a-z][a-z0-9-]*$/.test(formData.workflowDefinitionCode))
      errors.workflowDefinitionCode = 'Must be kebab-case';

    if (formData.detailPanelCode && !/^[a-z][a-z0-9-]*$/.test(formData.detailPanelCode))
      errors.detailPanelCode = 'Must be kebab-case';

    if (formData.displayOrder < 0)
      errors.displayOrder = 'Must be non-negative';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input,textarea')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.caseTypeCode.trim().toUpperCase();
      const name = formData.displayName.trim();
      const description = formData.description.trim() || undefined;
      const iconName = formData.iconName.trim() || undefined;
      const workflowDefinitionCode = formData.workflowDefinitionCode.trim() || undefined;
      const detailPanelCode = formData.detailPanelCode.trim() || undefined;

      if (mode === 'create') {
        await caseTypeService.create({
          caseTypeCode: code,
          displayName: name,
          description,
          iconName,
          workflowDefinitionCode,
          detailPanelCode,
          defaultModalSize: formData.defaultModalSize,
          isSecuredOnly: formData.isSecuredOnly,
          displayOrder: formData.displayOrder,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Case type "${code}" created successfully.`);
      } else if (record) {
        await caseTypeService.update(record.id, {
          caseTypeCode: code,
          displayName: name,
          description,
          iconName,
          workflowDefinitionCode,
          detailPanelCode,
          defaultModalSize: formData.defaultModalSize,
          isSecuredOnly: formData.isSecuredOnly,
          displayOrder: formData.displayOrder,
          statusCode: formData.statusCode,
          rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Case type "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This case type was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A case type with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        const serverErrors: string[] = err.response.data.errors;
        notificationService.toast.error(serverErrors.join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save case type. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add Case Type' : 'Edit Case Type'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="caseTypeCode">
              <label className="form-label">
                Case Type Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('caseTypeCode') ? ' form-field-error' : ''}`}
                value={formData.caseTypeCode}
                maxLength={40}
                placeholder="ARBITRATION, NI_ACT_138, …"
                onChange={(e) => {
                  clearFieldError('caseTypeCode');
                  setFormData(prev => ({ ...prev, caseTypeCode: e.target.value.toUpperCase() }));
                }}
                disabled={isSaving || mode === 'edit'}
              />
              {getFieldError('caseTypeCode') && (
                <div className="form-error-message">{getFieldError('caseTypeCode')}</div>
              )}
            </div>

            <div className="form-group" data-field="displayName">
              <label className="form-label">
                Display Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('displayName') ? ' form-field-error' : ''}`}
                value={formData.displayName}
                maxLength={100}
                placeholder="Arbitration"
                onChange={(e) => {
                  clearFieldError('displayName');
                  setFormData(prev => ({ ...prev, displayName: e.target.value }));
                }}
                disabled={isSaving}
              />
              {getFieldError('displayName') && (
                <div className="form-error-message">{getFieldError('displayName')}</div>
              )}
            </div>
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              rows={2}
              value={formData.description}
              placeholder="Long-form description; surfaces as tooltip in case-creation picker."
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              disabled={isSaving}
            />
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="iconName">
              <label className="form-label">Icon Name</label>
              <input
                type="text"
                className={`form-input${getFieldError('iconName') ? ' form-field-error' : ''}`}
                value={formData.iconName}
                maxLength={40}
                placeholder="gavel, shield, award…"
                onChange={(e) => {
                  clearFieldError('iconName');
                  setFormData(prev => ({ ...prev, iconName: e.target.value }));
                }}
                disabled={isSaving}
              />
              {getFieldError('iconName') && (
                <div className="form-error-message">{getFieldError('iconName')}</div>
              )}
            </div>

            <div className="form-group" data-field="defaultModalSize">
              <SyutSelect
                label="Default Modal Size"
                required
                options={MODAL_SIZE_OPTIONS}
                value={formData.defaultModalSize}
                onChange={(val) => setFormData(prev => ({
                  ...prev,
                  defaultModalSize: ((val as string) || 'xl') as ModalSize,
                }))}
                isClearable={false}
                isDisabled={isSaving}
              />
            </div>

            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                min={0}
                className={`form-input${getFieldError('displayOrder') ? ' form-field-error' : ''}`}
                value={formData.displayOrder}
                onChange={(e) => {
                  clearFieldError('displayOrder');
                  const v = parseInt(e.target.value, 10);
                  setFormData(prev => ({ ...prev, displayOrder: Number.isFinite(v) ? v : 0 }));
                }}
                disabled={isSaving}
              />
              {getFieldError('displayOrder') && (
                <div className="form-error-message">{getFieldError('displayOrder')}</div>
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="workflowDefinitionCode">
              <label className="form-label">Workflow Definition Code</label>
              <input
                type="text"
                className={`form-input${getFieldError('workflowDefinitionCode') ? ' form-field-error' : ''}`}
                value={formData.workflowDefinitionCode}
                maxLength={60}
                placeholder="arbitration-case"
                onChange={(e) => {
                  clearFieldError('workflowDefinitionCode');
                  setFormData(prev => ({ ...prev, workflowDefinitionCode: e.target.value.toLowerCase() }));
                }}
                disabled={isSaving}
              />
              {getFieldError('workflowDefinitionCode') && (
                <div className="form-error-message">{getFieldError('workflowDefinitionCode')}</div>
              )}
            </div>

            <div className="form-group" data-field="detailPanelCode">
              <label className="form-label">Detail Panel Code</label>
              <input
                type="text"
                className={`form-input${getFieldError('detailPanelCode') ? ' form-field-error' : ''}`}
                value={formData.detailPanelCode}
                maxLength={60}
                placeholder="arbitration-detail"
                onChange={(e) => {
                  clearFieldError('detailPanelCode');
                  setFormData(prev => ({ ...prev, detailPanelCode: e.target.value.toLowerCase() }));
                }}
                disabled={isSaving}
              />
              {getFieldError('detailPanelCode') && (
                <div className="form-error-message">{getFieldError('detailPanelCode')}</div>
              )}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="isSecuredOnly">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isSaving ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isSecuredOnly}
                  onChange={(e) => setFormData(prev => ({ ...prev, isSecuredOnly: e.target.checked }))}
                  disabled={isSaving}
                />
                <span>Secured-only (e.g. SARFAESI)</span>
              </label>
            </div>

            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                required
                options={STATUS_OPTIONS}
                value={formData.statusCode}
                onChange={(val) => setFormData(prev => ({
                  ...prev,
                  statusCode: ((val as string) || 'ACTIVE') as CaseTypeStatus,
                }))}
                isClearable={false}
                isDisabled={isSaving}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'create' ? 'Save' : 'Update'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create case type "${formData.caseTypeCode.trim().toUpperCase()}"?`
            : `Update case type "${formData.caseTypeCode.trim().toUpperCase()}"?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CaseTypeFormModal;
